import React, { createContext, useContext, useState, useCallback, useEffect } from "react"
import { useLocation } from "react-router-dom"

const BreadcrumbContext = createContext(null)

export function BreadcrumbProvider({ children }) {
  const [dynamicLabels, setDynamicLabels] = useState({})
  const location = useLocation()

  // Clear dynamic labels when route changes to a different base path
  useEffect(() => {
    // We keep labels for the current path and its parents
    // This allows child routes to set labels that persist while navigating within the same detail view
  }, [location.pathname])

  const setDynamicLabel = useCallback((pathPattern, label) => {
    setDynamicLabels((prev) => ({
      ...prev,
      [pathPattern]: label,
    }))
  }, [])

  const clearDynamicLabel = useCallback((pathPattern) => {
    setDynamicLabels((prev) => {
      const newLabels = { ...prev }
      delete newLabels[pathPattern]
      return newLabels
    })
  }, [])

  const clearAllDynamicLabels = useCallback(() => {
    setDynamicLabels({})
  }, [])

  const getDynamicLabel = useCallback(
    (path) => {
      // First try exact match
      if (dynamicLabels[path]) {
        return dynamicLabels[path]
      }

      // Try pattern matching for paths with IDs
      for (const [pattern, label] of Object.entries(dynamicLabels)) {
        // Convert pattern like "/staff/:id" to regex
        const regexPattern = pattern.replace(/:[\w]+/g, "[^/]+")
        const regex = new RegExp(`^${regexPattern}$`)
        if (regex.test(path)) {
          return label
        }
      }

      return null
    },
    [dynamicLabels]
  )

  return (
    <BreadcrumbContext.Provider
      value={{
        dynamicLabels,
        setDynamicLabel,
        clearDynamicLabel,
        clearAllDynamicLabels,
        getDynamicLabel,
      }}
    >
      {children}
    </BreadcrumbContext.Provider>
  )
}

export function useBreadcrumb() {
  const context = useContext(BreadcrumbContext)
  if (!context) {
    throw new Error("useBreadcrumb must be used within a BreadcrumbProvider")
  }
  return context
}

// Custom hook for setting breadcrumb on mount and clearing on unmount
export function useDynamicBreadcrumb(pathPattern, label) {
  const { setDynamicLabel, clearDynamicLabel } = useBreadcrumb()

  useEffect(() => {
    if (label) {
      setDynamicLabel(pathPattern, label)
    }

    return () => {
      clearDynamicLabel(pathPattern)
    }
  }, [pathPattern, label, setDynamicLabel, clearDynamicLabel])
}

export default BreadcrumbContext
